import { checkNullish } from '@/helpers/checkNullish';
import { IPaginationResponse } from '@/interfaces/common.interface';
import { ICollectionAvailableDocument } from '@/interfaces/doccen-lib/collection.interface';
import {
  EContentVisibility,
  IMaterialDetail,
  IResponseDetailMaterial,
} from '@/interfaces/doccen-lib/contentManagement.interface';
import { AxiosRequestConfig } from 'axios';
import { QueryFunctionContext } from 'react-query';
import { tmsHTTPRequest } from 'services/axios/httpRequest';

export const getDoccenLibManagementMaterials = ({
  categoryId,
  visibility,
}: {
  visibility: EContentVisibility;
  categoryId?: string | number;
}) =>
  tmsHTTPRequest.get<{ materials: IMaterialDetail[] }>(
    `/v1/materials/?visibility=${visibility}${
      categoryId ? `&categoryId=${categoryId}` : ''
    }`
  );

export const getDoccenCollectionAvailableDocuments = async ({
  pageParam = 1,
  queryKey,
}: QueryFunctionContext<(string | number)[]>) => {
  const [, collectionId, pageSize, query] = queryKey;

  const queryParams = new URLSearchParams({
    pageNumber: pageParam.toString(),
    pageSize: (String(pageSize) || 4).toString(),
    collectionId: collectionId.toString(),
  });

  if (checkNullish(query)) {
    queryParams.append('query', query.toString());
  }

  return tmsHTTPRequest.get<IPaginationResponse<ICollectionAvailableDocument>>(
    `/v1/materials/available?${queryParams.toString()}`
  );
};

export const postDoccenUploadFile = (file: File) =>
  tmsHTTPRequest
    .get<{
      key: string;
      url: string;
    }>(`/v1/materials/presigned-url?contentType=${file.type}`)
    .then(async (res) => {
      await fetch(res.url, {
        method: 'put',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });
      return res;
    });

export const getDoccenLibMaterial = (
  materialId: string,
  config?: AxiosRequestConfig
): Promise<IResponseDetailMaterial> =>
  tmsHTTPRequest.get(`/v1/materials/${materialId}`, config);

export const postDoccenLibMaterial = (req: {
  category: number;
  description: string;
  key: string;
  metaData: {
    contentType: string;
    fileName: string;
  };
  name: string;
  visibility: EContentVisibility;
}) => tmsHTTPRequest.post<{ id: string }>(`/v1/materials`, req);

export const patchDoccenLibMaterial = (req: {
  materialId: string;
  category: number;
  description: string;
  name: string;
  visibility: EContentVisibility;
}) => {
  const { materialId, ...restProps } = req;

  return tmsHTTPRequest.patch(`/v1/materials/${materialId}`, restProps);
};

export const deleteDoccenLibMaterial = (materialId: string) =>
  tmsHTTPRequest.delete(`/v1/materials/${materialId}`);

export const doceenLibMaterialAI = (materialId: string) =>
  tmsHTTPRequest.post<{
    conversationId: string;
  }>(`/v1/materials/${materialId}/doccen-ai`, {
    materialId,
  });

export const postSearchDoccenLibMaterial = async (body: {
  categories: number[] | null;
  pageSize: number;
  pageNumber: number;
  query?: string;
}) =>
  tmsHTTPRequest.post<IPaginationResponse<IMaterialDetail>>(
    `/v1/materials/search`,
    body
  );
